/**
 * Calculator kill switches (ENG-2841) — the single source of truth mapping
 * calculator surfaces (route names, product labels/slugs) to their flags.
 *
 * Semantics: the bare `calculators` domain flag kills everything at once;
 * a per-calculator flag kills that calculator's nav/tiles/routes. Surfaces
 * not in these maps (unknown labels, non-calculator routes) are never blocked.
 */
import {
    FLAG_CALCULATORS,
    FLAG_CALCULATORS_CLAT,
    FLAG_CALCULATORS_COMPARISON,
    FLAG_CALCULATORS_COMPARISON_CAPITAL_GAINS,
    FLAG_CALCULATORS_COMPARISON_ESTATE,
    FLAG_CALCULATORS_COMPARISON_ORDINARY_INCOME,
    FLAG_CALCULATORS_CRUT,
    FLAG_CALCULATORS_ESTATE_TAX_BURN,
    FLAG_CALCULATORS_GRAT,
    FLAG_CALCULATORS_IRA,
    FLAG_CALCULATORS_NGT,
    FLAG_CALCULATORS_OIL_GAS,
    FLAG_CALCULATORS_PPLI,
    FLAG_CALCULATORS_QSBS,
    FLAG_CALCULATORS_SOLAR,
    isFeatureEnabled,
} from '@/store/feature_flags';

export const CALCULATOR_FLAG_BY_ROUTE = {
    'charitable-remainder-trust-calculator': FLAG_CALCULATORS_CRUT,
    'charitable-remainder-trust-calculator-shared': FLAG_CALCULATORS_CRUT,
    'charitable-remainder-trust-calculator-test-cases': FLAG_CALCULATORS_CRUT,
    'charitable-remainder-trust-report': FLAG_CALCULATORS_CRUT,
    'clat-calculator': FLAG_CALCULATORS_CLAT,
    'clat-calculator-shared': FLAG_CALCULATORS_CLAT,
    'grat-calculator': FLAG_CALCULATORS_GRAT,
    'grat-calculator-shared': FLAG_CALCULATORS_GRAT,
    'qsbs-calculator': FLAG_CALCULATORS_QSBS,
    'qsbs-calculator-shared': FLAG_CALCULATORS_QSBS,
    'solar-calculator': FLAG_CALCULATORS_SOLAR,
    'ira-calculator': FLAG_CALCULATORS_IRA,
    'oil-gas-calculator': FLAG_CALCULATORS_OIL_GAS,
    'ppli-calculator': FLAG_CALCULATORS_PPLI,
    'ngt-calculator': FLAG_CALCULATORS_NGT,
    'estate-tax-burn-calculator': FLAG_CALCULATORS_ESTATE_TAX_BURN,
    'compare-capital-gains-tax-savings': [FLAG_CALCULATORS_COMPARISON, FLAG_CALCULATORS_COMPARISON_CAPITAL_GAINS],
    'compare-ordinary-income-tax-savings': [FLAG_CALCULATORS_COMPARISON, FLAG_CALCULATORS_COMPARISON_ORDINARY_INCOME],
    'compare-estate-tax-savings': [FLAG_CALCULATORS_COMPARISON, FLAG_CALCULATORS_COMPARISON_ESTATE],
};

export const CALCULATOR_FLAG_BY_LABEL = {
    crut: FLAG_CALCULATORS_CRUT,
    'ira-crut-rollover': FLAG_CALCULATORS_IRA,
    'crut-qsbs-stacking': FLAG_CALCULATORS_QSBS,
    clat: FLAG_CALCULATORS_CLAT,
    solar: FLAG_CALCULATORS_SOLAR,
    'oil-gas': FLAG_CALCULATORS_OIL_GAS,
    grat: FLAG_CALCULATORS_GRAT,
    ppli: FLAG_CALCULATORS_PPLI,
    ngt: FLAG_CALCULATORS_NGT,
    qsbs: FLAG_CALCULATORS_QSBS,
    ira: FLAG_CALCULATORS_IRA,
};

export function isCalculatorsDomainEnabled() {
    return isFeatureEnabled(FLAG_CALCULATORS);
}

export function isCalculatorRouteEnabled(routeName) {
    if (!isCalculatorsDomainEnabled()) {
        return false;
    }
    const flags = CALCULATOR_FLAG_BY_ROUTE[routeName];
    return flags ? [].concat(flags).every(isFeatureEnabled) : true;
}

export function isCalculatorLabelEnabled(label) {
    if (!isCalculatorsDomainEnabled()) {
        return false;
    }
    const flag = CALCULATOR_FLAG_BY_LABEL[label];
    return flag ? isFeatureEnabled(flag) : true;
}
